import type { TyreLayoutSubjectKind } from '@chukua/common';
import { Checkbox, Segmented } from 'antd';
import clsx from 'clsx';
import { useState } from 'react';
import { bodyStyleFor, canCarryContainer } from '@chukua/vehicle-model';
import type { DiagramPart } from '../policies/towed-codes';
import { TyrePositionDiagram, type TyreDiagramAxle, type TyreDiagramTowed, type TyreDiagramView } from '../public/TyrePositionDiagram';

interface TyreLayoutCanvasProps {
  /** Replaces the editor's sticky placement. */
  className?: string;
  subjectKind: TyreLayoutSubjectKind;
  bodyStyle: string | null;
  axles: TyreDiagramAxle[];
  view: TyreDiagramView;
  onViewChange: (view: TyreDiagramView) => void;
  selectedCode: string | null;
  onSelect: (code: string, part: DiagramPart) => void;
  /** A particular vehicle's registration, for its number plates; omitted on a class or type's own page. */
  registration?: string | null;
  /** That vehicle's own paint. */
  color?: string | null;
  /** A truck's trailer, drawn coupled behind it. */
  towed?: TyreDiagramTowed | null;
}

const VIEW_OPTIONS: { label: string; value: TyreDiagramView }[] = [
  { label: '3D', value: '3d' },
  { label: 'Side', value: 'side' },
  { label: 'Front', value: 'front' },
  { label: 'Plan', value: 'plan' },
];

/** What the 3D view shows over the vehicle: its codes, through its body, and a container where one fits. */
function useDisplayOptions() {
  const [showCodes, setShowCodes] = useState(true);
  const [isSeeThrough, setSeeThrough] = useState(false);
  const [showContainer, setShowContainer] = useState(false);
  return { showCodes, setShowCodes, isSeeThrough, setSeeThrough, showContainer, setShowContainer };
}

function DisplayOptions({ options, canShowContainer }: { options: ReturnType<typeof useDisplayOptions>; canShowContainer: boolean }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
      <Checkbox checked={options.showCodes} onChange={(event) => options.setShowCodes(event.target.checked)}>
        Codes
      </Checkbox>
      <Checkbox checked={options.isSeeThrough} onChange={(event) => options.setSeeThrough(event.target.checked)}>
        See through body
      </Checkbox>
      {canShowContainer && (
        <Checkbox checked={options.showContainer} onChange={(event) => options.setShowContainer(event.target.checked)}>
          Container
        </Checkbox>
      )}
    </div>
  );
}

/** The drawing with its view switch and display options: the left of the page, as the map is on the Zones page, kept in view while the axles scroll. */
export function TyreLayoutCanvas({
  className,
  subjectKind,
  bodyStyle,
  axles,
  view,
  onViewChange,
  selectedCode,
  onSelect,
  registration,
  color,
  towed,
}: TyreLayoutCanvasProps) {
  const options = useDisplayOptions();
  const { showCodes, isSeeThrough, showContainer } = options;
  const is3d = view !== 'plan';
  const canShowContainer =
    canCarryContainer(bodyStyleFor(subjectKind, bodyStyle)) || Boolean(towed && canCarryContainer(bodyStyleFor('trailer_type', towed.bodyStyle)));
  return (
    <section
      aria-label="Tyre position diagram"
      className={clsx('min-w-0 overflow-hidden rounded-lg border border-line bg-surface', className ?? 'lg:sticky lg:top-4')}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <Segmented<TyreDiagramView> options={VIEW_OPTIONS} value={view} onChange={onViewChange} aria-label="View" />
        {is3d ? (
          <DisplayOptions options={options} canShowContainer={canShowContainer} />
        ) : (
          <p className="m-0 text-xs text-muted">
            <span className="font-medium text-fg">Click a tyre</span> to select its position · front on the right
          </p>
        )}
      </div>
      <TyrePositionDiagram
        className={is3d ? undefined : 'px-4 py-5'}
        subjectKind={subjectKind}
        bodyStyle={bodyStyle}
        axles={axles}
        view={view}
        selectedCode={selectedCode}
        onSelect={onSelect}
        showCodes={showCodes}
        isSeeThrough={isSeeThrough}
        showContainer={showContainer}
        registration={registration}
        color={color}
        towed={towed}
      />
    </section>
  );
}
